import { createFileRoute, Link } from "@tanstack/react-router";
import { Clock, PlaneLanding, ShieldCheck, Wallet } from "lucide-react";

import { CarGrid } from "@/components/site/CarGrid";
import { LocationMap } from "@/components/site/LocationMap";
import { Button } from "@/components/ui/button";
import { cars } from "@/data/cars";
import { getPickupPoint } from "@/data/locations";
import { hreflangLinks } from "@/lib/i18n";
import { publicPageHeaders } from "@/lib/http-cache";

const CANONICAL = "https://carentals.ro/ro/inchirieri-auto-otopeni";
const title = "Închirieri auto Otopeni — predare gratuită, fără garanție";
const description =
  "Rent a car Otopeni cu predare gratuită la aeroport, fără garanție și fără card de credit. Asigurare și rovinietă incluse, non-stop, de la 44 €/zi.";

export const Route = createFileRoute("/ro/inchirieri-auto-otopeni")({
  headers: publicPageHeaders,
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: CANONICAL },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "ro_RO" },
      {
        name: "keywords",
        content:
          "inchirieri auto otopeni, rent a car otopeni, inchirieri masini aeroport otopeni, inchirieri auto bucuresti aeroport",
      },
    ],
    links: [
      { rel: "canonical", href: CANONICAL },
      ...hreflangLinks("/car-rental-otopeni-airport", "/ro/inchirieri-auto-otopeni"),
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Închirieri auto la Aeroportul Otopeni (OTP)",
          serviceType: "Închirieri auto aeroport",
          url: CANONICAL,
          provider: { "@id": "https://carentals.ro/#organization" },
          areaServed: {
            "@type": "Airport",
            name: "Aeroportul Internațional Henri Coandă",
            iataCode: "OTP",
            address: {
              "@type": "PostalAddress",
              addressLocality: "Otopeni",
              addressCountry: "RO",
            },
          },
          offers: {
            "@type": "Offer",
            price: "44",
            priceCurrency: "EUR",
            availability: "https://schema.org/InStock",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              q: "Predarea la aeroport este gratuită?",
              a: "Da. Predăm și preluăm mașina la Aeroportul Henri Coandă (Otopeni) gratuit, la orice oră, inclusiv pentru zboruri de noapte.",
            },
            {
              q: "Am nevoie de garanție sau card de credit la aeroport?",
              a: "Nu. Nu cerem garanție și nu blocăm sume pe card. Este suficient un card de debit, numerar în EUR sau RON, ori transfer bancar.",
            },
            {
              q: "Unde ne întâlnim în aeroport?",
              a: "Colegul nostru vă așteaptă în zona de Sosiri cu o plăcuță cu numele dumneavoastră și vă conduce la mașină, în parcarea de scurtă durată.",
            },
            {
              q: "Ce se întâmplă dacă zborul întârzie?",
              a: "Trimiteți-ne numărul zborului la rezervare. Îl monitorizăm și ajustăm ora predării fără costuri suplimentare.",
            },
          ].map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Page,
});

const avantaje = [
  {
    icon: PlaneLanding,
    title: "Predare gratuită la OTP",
    text: "Vă așteptăm în zona de Sosiri cu plăcuță cu nume și predăm mașina în parcarea aeroportului.",
  },
  {
    icon: Clock,
    title: "Non-stop, zbor monitorizat",
    text: "Aterizări noaptea sau întârzieri — trimiteți numărul zborului și ajustăm gratuit ora.",
  },
  {
    icon: Wallet,
    title: "Fără garanție, fără card credit",
    text: "Nu blocăm nimic pe card. Card de debit, numerar în EUR/RON sau transfer bancar.",
  },
  {
    icon: ShieldCheck,
    title: "Asigurare și rovinietă incluse",
    text: "RCA și CASCO, rovinietă și kilometraj nelimitat în România, incluse în tarif.",
  },
];

function Page() {
  const otopeni = getPickupPoint("otopeni");

  return (
    <>
      <section className="bg-ink py-20 text-ink-foreground">
        <div className="container-page">
          <p className="eyebrow">Aeroportul Henri Coandă (OTP) · Predare gratuită</p>
          <h1 className="mt-4 max-w-3xl text-4xl font-extrabold leading-[1.05] sm:text-5xl">
            Închirieri auto la Aeroportul Otopeni
          </h1>
          <p className="mt-5 max-w-2xl text-base text-ink-foreground/80">
            Aterizezi la Otopeni și pleci la drum în câteva minute. Aducem mașina la aeroport
            gratuit, zi sau noapte, fără garanție și fără card de credit — asigurarea, rovinieta și
            kilometrajul nelimitat sunt deja incluse în tariful zilnic.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Button asChild variant="gold" size="lg">
              <Link to="/rentals">Rezervă acum</Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-white/30 bg-transparent text-ink-foreground hover:bg-white/10 hover:text-ink-foreground"
            >
              <a href="https://wa.me/40738792499" target="_blank" rel="noreferrer">
                Trimite numărul zborului
              </a>
            </Button>
          </div>
        </div>
      </section>

      <section className="container-page py-20">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {avantaje.map((p) => (
            <div key={p.title} className="rounded-xl border border-border bg-card p-6 shadow-card">
              <p.icon className="size-6 text-gold" />
              <h2 className="mt-4 font-display text-lg font-bold">{p.title}</h2>
              <p className="mt-2 text-sm text-muted-foreground">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="container-page pb-20">
        <div className="grid items-start gap-10 lg:grid-cols-2">
          <div>
            <p className="eyebrow">Punct de predare</p>
            <h2 className="mt-3 text-3xl font-bold">Unde ne întâlnim la Otopeni</h2>
            <p className="mt-4 text-muted-foreground">
              Aeroportul Henri Coandă se află la 17 km nord de centrul Bucureștiului, aproximativ
              30–40 de minute cu mașina pe DN1. După ce ieșiți din zona de Sosiri, predarea cheilor
              și semnarea contractului durează în jur de zece minute.
            </p>
            <p className="mt-4 text-muted-foreground">
              Returnarea funcționează la fel: lăsați mașina la terminal înainte de zbor, fără costuri
              suplimentare. Predarea la biroul nostru din Bulevardul Regina Elisabeta 33 este de
              asemenea gratuită, iar livrarea la altă adresă din București costă 20 €.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild variant="gold">
                <Link to="/rentals">Vezi mașinile disponibile</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/ro/inchirieri-auto-fara-garantie">Închiriere fără garanție</Link>
              </Button>
            </div>
          </div>
          <LocationMap point={otopeni} height="h-72" />
        </div>
      </section>

      <section className="bg-secondary/60 py-20">
        <div className="container-page">
          <h2 className="text-3xl font-bold sm:text-4xl">Mașini livrate la Otopeni</h2>
          <CarGrid cars={cars.slice(0, 6)} className="mt-10" />
          <div className="mt-8">
            <Button asChild variant="outline">
              <Link to="/rentals">Vezi toate mașinile</Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
